import { useEffect, useState } from "react";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useDispatch } from "react-redux";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { setToken } from "../../core/feature/token/tokenSlice";
import { setUser } from "../../core/feature/user/userSlice";
import { credentialCheck } from "../../core/service/api";
import { ChevronLeft, Info } from "lucide-react";
import { getDetailMutationSaving } from "../../core/service/api_saving";
import { ReversalIcon, SavingIcon } from "./icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { i18n } from "../../translations/i18n";
const ReceiptMobile = (props: any) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  const callerIdFromUrl = searchParams.get("callerId");
  const coaFromUrl = searchParams.get("coa");

  const [activeCallerId, setActiveCallerId] = useState<string | null>(null);
  const [activeCoa, setActiveCoa] = useState<string | null>(null);
  const [callerIdentities, setCallerIdentities] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);
  useEffect(() => {
    console.log(location.state);

    if (location.state) setDetail(location.state.data);
  }, [location.state]);

  useEffect(() => {
    const storedToken = localStorage.getItem("token_redux");
    if (token) {
      localStorage.setItem("token_redux", token);
      dispatch(setToken(token));
    } else if (!storedToken) {
      navigate("/");
      return;
    }
    credentialCheck()
      .then((res) => {
        dispatch(setUser(res.data.user));
        const identities = res.data.user?.accounts?.[0]?.callerIdentities ?? [];
        if (!identities.length) {
          navigate("/");
          return;
        }
        if (!callerIdFromUrl || !coaFromUrl) return;
        setCallerIdentities(identities);
        setActiveCallerId(callerIdFromUrl);
        setActiveCoa(coaFromUrl);
      })
      .catch(() => navigate("/"));
  }, [token, callerIdFromUrl, coaFromUrl]);

  useEffect(() => {
    if (!id) return;
    getDetailMutationSaving(id).then((res: any) => {
      setDetail(res.data);
    });
  }, [id]);

  if (!detail || (location.state && !location.state.data)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading
      </div>
    );
  }

  type MutationStatus = "SUCCESS" | "REVERSED";
  type MutationType = "SAVING" | "CASHOUT" | "REVERSED";
  const getMutationType = (item: {
    status: MutationStatus;
    credit: number;
    debit: number;
  }): MutationType => {
    if (item.status === "REVERSED") return "REVERSED";
    if (Number(item.credit) > 0) return "SAVING";
    return "CASHOUT";
  };
  const mutationType: MutationType = location.state
    ? "SAVING"
    : detail
      ? getMutationType(detail)
      : "SAVING";
  const mutationTypeDetail = {
    SAVING: {
      title: `${t("SAVING.saving")} ${t("SAVING.success")}`,
      type: t("SAVING.saving"),
      background: "bg-[#0054E6]",
      Icon: SavingIcon,
    },
    CASHOUT: {
      title: `${t("SAVING.cashout")} ${t("SAVING.success")}`,
      type: t("SAVING.cashout"),
      background: "bg-[#EA0C0C]",
      Icon: SavingIcon,
    },
    REVERSED: {
      title: `${t("SAVING.reversed")} ${t("SAVING.success")}`,
      type: t("SAVING.reversed"),
      background: "bg-[#FFBC00]",
      Icon: ReversalIcon,
    },
  } as const;

  const { title, type, background, Icon } = mutationTypeDetail[mutationType];

  return (
    <div className="relative min-h-screen h-full z-50 bg-psp-primary pb-10">
      <div className="min-h-screen h-full mb-6 px-4 py-6 bg-psp-primary top-to-bottom">
        <div className="flex d-flex w-full items-center">
          <button
            onClick={() =>
              navigate(`/saving?callerId=${callerIdFromUrl}&coa=${coaFromUrl}`)
            }
          >
            <ChevronLeft className="bg-white w-8 h-8 rounded-lg border p-1" />
          </button>
          <div className="absolute left-1/2 -translate-x-1/2 font-bold text-md">
            {t("SAVING.savingsDetail")}
          </div>
        </div>
        <section className="mt-12 mb-8 flex flex-col gap-3 items-center justify-center">
          <div
            className={`w-20 h-20 ${background} flex flex-col px-3 justify-center text-center rounded-full`}
          >
            <Icon className="w-20 h-20" />
          </div>
        </section>
        <section className="mb-14 flex flex-col gap-3">
          <div className="flex flex-col px-3 justify-center text-center font-montserrat">
            <h1 style={{ fontSize: 18 }} className={`font-bold text-sm mb-2`}>
              {title}
            </h1>
            <h1 style={{ fontSize: 14 }} className={`mb-2`}>
              {moment(detail.dateTime).locale(i18n.language).format("LL")}
            </h1>
            <h1 style={{ fontSize: 14 }}>
              {new Date(detail.dateTime).toLocaleTimeString("id-ID", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </h1>
          </div>
        </section>
        <section className="mb-6">
          <div className="flex flex-col d-flex">
            <span className="d-flex justify-content-between">
              <p>{t("SAVING.transactionType")}</p>
              <p className="mt-0 text-right break-words max-w-[50%]">{type}</p>
            </span>
            <span className="d-flex justify-content-between">
              <p>{t("SAVING.transactionTime")}</p>
              <p className="mt-0">
                {new Date(detail.dateTime).toLocaleDateString("id-ID", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </span>
            <span className="d-flex justify-content-between">
              <p>NIS</p>
              <p className="mt-0">
                {detail.callerId || location.state.activeUser.callerId || "-"}
              </p>
            </span>
            <span className="d-flex justify-content-between">
              <p>{t("SAVING.callerName")}</p>
              <p className="mt-0 text-right break-words max-w-[50%]">
                {detail.callerName}
              </p>
            </span>
            <span className="d-flex justify-content-between">
              <p>{t("SAVING.note")}</p>
              <p className="mt-0 text-right break-words max-w-[50%]">
                {detail.note || "-"}
              </p>
            </span>
            <strong>
              <span className="d-flex justify-content-between">
                <p>{t("SAVING.total")}</p>
                <p className="mt-0">
                  Rp.{" "}
                  {Number(
                    detail.credit || detail.debit || detail.amount,
                  ).toLocaleString("id-ID")}
                </p>
              </span>
            </strong>
          </div>
        </section>
        <section>
          <div className="flex flex-row justify-between w-full">
            <div className="w-full h-16 bg-[#FFF685] rounded-xl mb-8">
              <span className="d-flex justify-content-between px-3 mt-2">
                <Info />
                <p className="ml-2">{t("SAVING.info")}</p>
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReceiptMobile;
